import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type CartTotalsResponse, type ProductDto } from "../api";

export interface ChosenModifier {
  groupId: string;
  optionId: string;
  title: string;
  price: number;
  qty: number;
  exclusion: boolean;
}

export interface CartItem {
  productId: string;
  title: string;
  imageUrl: string | null;
  qty: number;
  modifiers: ChosenModifier[];
}

const STORAGE_KEY = "ft.cart.v2";
const PREVIEW_DELAY_MS = 250;

const EMPTY_TOTALS: CartTotalsResponse = {
  lines: [],
  baseSubtotal: 0,
  promotionDiscount: 0,
  subtotal: 0,
  discount: 0,
  total: 0,
  promo: null,
  promoId: null,
  appliedPromotions: []
};

/** Две позиции считаются одинаковыми, если совпадают товар и весь набор модификаторов. */
function sameItem(left: CartItem, right: CartItem): boolean {
  if (left.productId !== right.productId) return false;
  if (left.modifiers.length !== right.modifiers.length) return false;

  const key = (item: CartItem) =>
    item.modifiers
      .map(modifier => `${modifier.groupId}:${modifier.optionId}:${modifier.qty}`)
      .sort()
      .join("|");

  return key(left) === key(right);
}

function readStored(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

/**
 * Корзина гостя.
 *
 * Состав хранится локально и переживает случайное закрытие вкладки, а вот
 * суммы всегда считает сервер: акции и промокоды живут в базе и меняются
 * администратором, повторять их правила в браузере — врать гостю о цене.
 */
export function useCart(promoCode: string) {
  const [items, setItems] = useState<CartItem[]>(readStored);
  const [totals, setTotals] = useState<CartTotalsResponse>(EMPTY_TOTALS);
  const [pricing, setPricing] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const payload = useMemo(
    () =>
      items.map(item => ({
        productId: item.productId,
        qty: item.qty,
        modifiers: item.modifiers.map(modifier => ({
          groupId: modifier.groupId,
          optionId: modifier.optionId,
          qty: modifier.qty
        }))
      })),
    [items]
  );

  useEffect(() => {
    if (payload.length === 0) {
      setTotals(EMPTY_TOTALS);
      return;
    }

    // Пересчёт не дёргается на каждое нажатие: гость крутит количество быстрее,
    // чем отвечает сеть.
    window.clearTimeout(timer.current);
    setPricing(true);

    timer.current = window.setTimeout(() => {
      api
        .post<CartTotalsResponse>("/api/public/cart", { items: payload, promoCode: promoCode.trim() || undefined })
        .then(setTotals)
        .catch(() => {
          // Сеть моргнула — оставляем последние известные суммы, не выдумывая новые.
        })
        .finally(() => setPricing(false));
    }, PREVIEW_DELAY_MS);

    return () => window.clearTimeout(timer.current);
  }, [payload, promoCode]);

  const add = useCallback((product: ProductDto, modifiers: ChosenModifier[], qty = 1) => {
    setItems(current => {
      const incoming: CartItem = {
        productId: product.id,
        title: product.title,
        imageUrl: product.imageUrl,
        qty,
        modifiers
      };

      const index = current.findIndex(item => sameItem(item, incoming));
      if (index === -1) return [...current, incoming];

      return current.map((item, position) =>
        position === index ? { ...item, qty: item.qty + qty } : item
      );
    });
  }, []);

  const setQty = useCallback((index: number, qty: number) => {
    setItems(current =>
      qty <= 0
        ? current.filter((_, position) => position !== index)
        : current.map((item, position) => (position === index ? { ...item, qty } : item))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, item) => sum + item.qty, 0);

  return { items, totals, pricing, count, add, setQty, clear };
}
